import React, { Dispatch, SetStateAction, useState } from 'react'
import moment from 'moment'
import { Tooltip, Typography } from '@mui/material'
import { defaultColors } from '../../infrastructure/materialUiThemeProvider'
import { t } from '../../infrastructure/i18nextHelper'
import { FeatureContainer } from '../../infrastructure/feature'
import { hasClaim } from '../../infrastructure/signIn/userContext'
import { Claims } from '../../infrastructure/signIn/models'
import { ColumnDescriptor, Link } from '../common/customComponents'
import {
    ActualStockStatus, MovementType, MktSaleType, PurchaseType, StockProjection,
    StockSimulation, UntriggeredType
} from './stockModels'
import { StockBoardContainer } from './stockBoardStore'
import { SimulationBoardContainer } from './simulation/simulationBoardStore'
import { StockFiltersContainer } from './filters/filtersStore'
import {
    fieldSimulated, fieldWithSimulation, removeSimulationButton, simulatedProjection,
    simulatedProjectionDays, startEditSimulation
} from './_simulationFields'
import { StockProjectionDetailArgs, stockProjectionDetailDialog } from './stockProjectionDetailDialog'
import { TransitStockDetailArgs, transitStockDetailDialog } from './transitStockDetailDialog'
import { closedColor, frozenColor, openedColor } from './actualStockEdit/accountingPeriod/accountingPeriodDialog'
import { purchaseDealDetailDialog, PurchaseDealDetailArgs } from './purchaseDealDetailDialog'
import { UserSettingsContextContainer } from '../../infrastructure/shell/userSettings/userSettingsStore'
import { isAfterToday, isoDateFormat } from '../../infrastructure/date'

type StockBoardStore = ReturnType<typeof StockBoardContainer.useContainer>
type SimulationBoardStore = ReturnType<typeof SimulationBoardContainer.useContainer>
type StockFiltersStore = ReturnType<typeof StockFiltersContainer.useContainer>
type FeatureStore = ReturnType<typeof FeatureContainer.useContainer>
type SimulationEditing = { date: string, type: string } | null

type ProjectionColumnsOptions = {
    shortDateFormat: string
    usefulStock: boolean
    store: StockBoardStore
    simulationStore: SimulationBoardStore
    stockFiltersStore: StockFiltersStore
    feature: FeatureStore
    classes: any
    simulationEditing: SimulationEditing
    setSimulationEditing: Dispatch<SetStateAction<SimulationEditing>>
    handleSimulationValidated: (validation?: StockSimulation | null) => void
    changePurchaseSimulation: (simulation: StockSimulation, untriggeredVolume?: number | null) => void
    changeSimulation: (simulation: StockSimulation) => void
}

type ActualStockCalibrationProps = {
    actualStockStatus: ActualStockStatus,
    actualStock: number | null,
    date: string,
    companies: string[],
    dutyStatuses: string[],
    productIds: string[],
    sites: string[],
    shouldDisplayStockProjectionDetails: boolean,
}

type TransitStockDetailProps = {
    volumeInTransit: number | null,
    date: string,
    companys: string[],
    dutyStatuss: string[],
    productIds: string[],
    sites: string[],
    shouldDisplayTransitStockDetail: boolean,
}

export let getProjectionColumns = (
    options: ProjectionColumnsOptions,
    forSimulation: boolean = false
): ColumnDescriptor<StockProjection>[] => {
    let { shortDateFormat, usefulStock, store, simulationStore, stockFiltersStore, feature, classes } = options
    let projectionValues = store.stockProjectionResult.values
    let hasTransitVolume = feature.hasFeature('TransitVolume')
    let withTourKey = (tourKey: string) => forSimulation ? {} : { tourKey }

    return [
        {
            name: t('stock.label.stocks.date'),
            value: projection => projection.date ? moment(projection.date).format(shortDateFormat) : '',
            htmlFor: projection => forSimulation
                ? <SimulationDate projection={projection} shortDateFormat={shortDateFormat} />
                : <ProjectionDate
                    projection={projection}
                    shortDateFormat={shortDateFormat}
                    changeDate={store.changeStockDateFilter}
                />,
            ...withTourKey('stockProjectionTableDateColumn')
        }, {
            name: t('stock.label.stocks.purchase'),
            tooltipText: t('stock.label.stocks.purchaseTooltip', { unit: store.unit }),
            tooltipValue: projection => {
                let untriggeredVolume = getMovementVolume(projection, UntriggeredType)
                return untriggeredVolume
                    ? t('stock.label.stocks.purchaseValueTooltip', { untriggered: untriggeredVolume ?? 0, unit: store.unit })
                    : null
            },
            value: projection => getPurchaseValue(projection),
            htmlFor: projection => forSimulation
                ? <SimulationPurchase projection={projection} options={options} />
                : <ProjectionPurchase
                    projection={projection}
                    hasPurchaseDealDetail={feature.hasFeature('PurchaseDealDetail')}
                    onLinkClicked={() => openPurchaseDialog(projection, options)}
                />,
            total: forSimulation
                ? getSimulationPurchaseTotal(projectionValues, simulationStore.simulations)
                : getProjectionPurchaseTotal(projectionValues),
            totalLabel: t('components.table.sum'),
            ...withTourKey('stockProjectionTablePurchaseColumn'),
            textAlign: 'right',
            actionButtonAlign: 'left',
            cellAction: !forSimulation ? undefined : projection => {
                if (!isAfterToday(projection.date)) return <></>
                let simulation = simulationStore.simulations.find(item => item.date === projection.date && item.movementType === PurchaseType)
                if (simulation)
                    return removeSimulationButton(() => simulationStore.removeSimulation(projection.date, PurchaseType))
                if (options.simulationEditing?.date === projection.date && options.simulationEditing?.type === PurchaseType)
                    return <></>
                return startEditSimulation(() => options.setSimulationEditing({ date: projection.date, type: PurchaseType }))
            }
        }, {
            name: t('stock.label.stocks.mktSale'),
            tooltipText: t('stock.label.stocks.mktSaleTooltip', { unit: store.unit }),
            value: projection => getMktSaleValue(projection),
            htmlFor: projection => forSimulation
                ? <SimulationMktSale projection={projection} options={options} />
                : <ProjectionMktSale projection={projection} />,
            total: forSimulation
                ? getSimulationMktSaleTotal(projectionValues, simulationStore.simulations)
                : getProjectionMktSaleTotal(projectionValues),
            totalLabel: t('components.table.sum'),
            textAlign: 'right',
            ...withTourKey('stockProjectionTableMktSaleColumn'),
            actionButtonAlign: 'left',
            cellAction: !forSimulation ? undefined : projection => {
                if (!isAfterToday(projection.date)) return <></>
                let simulation = simulationStore.simulations.find(item => item.date === projection.date && item.movementType === MktSaleType)
                if (simulation)
                    return removeSimulationButton(() => simulationStore.removeSimulation(projection.date, MktSaleType))
                if (options.simulationEditing?.date === projection.date && options.simulationEditing?.type === MktSaleType)
                    return <></>
                return startEditSimulation(() => options.setSimulationEditing({ date: projection.date, type: MktSaleType }))
            }
        }, {
            name: t('stock.label.stocks.other'),
            tooltipText: t('stock.label.stocks.volumeOtherTooltip', { unit: store.unit }),
            value: projection => projection.volumeOther ? Math.round(projection.volumeOther).toString() : '',
            total: projectionValues?.reduce((acc, projection) => acc + (projection.volumeOther || 0), 0),
            totalLabel: t('components.table.sum'),
            textAlign: 'right',
            ...withTourKey('stockProjectionTableVolumeOtherColumn'),
            wrapHeaderText: true
        },
        ...(hasTransitVolume ? [{
            name: t('stock.label.stocks.transit'),
            tooltipText: t('stock.label.stocks.transitTooltip', { unit: store.unit }),
            value: projection => projection.volumeInTransit ? Math.round(projection.volumeInTransit).toString() : '',
            textAlign: 'right' as const,
            ...withTourKey('stockProjectionTableTransitVolumeColumn'),
            leftBorder: true,
            htmlFor: projection => <Transit projection={projection} options={options} />,
            wrapHeaderText: true
        }] : []),
        {
            name: usefulStock ? t('stock.label.stocks.usefulCalculated') : t('stock.label.stocks.calculated'),
            tooltipText: usefulStock
                ? t('stock.label.stocks.usefulCalculatedTooltip', { unit: store.unit })
                : t('stock.label.stocks.calculatedTooltip', { unit: store.unit }),
            value: projection => projection.calculatedStock != null ? Math.round(projection.calculatedStock).toString() : '',
            textAlign: 'right' as const,
            htmlFor: projection => <Calculated projection={projection} classes={classes} />,
            total: shouldDisplayProjectedStockTableAverage(options) ? getDecadeAverageProjectedStock(options) : null,
            totalLabel: shouldDisplayProjectedStockTableAverage(options) ? t('components.table.average') : null,
            ...withTourKey('stockProjectionTableProjectedColumn'),
            leftBorder: !hasTransitVolume,
            wrapHeaderText: true
        },
        ...(feature.hasFeature('StockPercentUsage') ? [{
            name: t('stock.label.stocks.stockPercent'),
            tooltipText: t('stock.label.stocks.stockPercentTooltip'),
            value: projection => getStockPercent(projection, stockFiltersStore.filters.stockPercentHigh),
            textAlign: 'right' as const,
            ...withTourKey('stockProjectionTableSimulatedColumn')
        }] : []),
        {
            name: t('stock.label.stocks.days'),
            tooltipText: t('stock.label.stocks.daysTooltip'),
            value: projection => projection.daysLeft ? projection.daysLeft.toString() : '',
            textAlign: 'right' as const,
            htmlFor: projection => <Days
                projection={projection}
                minDays={store.stockProjectionResult.minDays}
                classes={classes}
            />,
            ...withTourKey('stockProjectionTableDaysColumn'),
            totalLabel: t('stock.label.stocks.stockVariation'),
            total: computeStockVariation(store)
        }, {
            name: usefulStock ? t('stock.label.stocks.usefulActual') : t('stock.label.stocks.actual'),
            tooltipText: usefulStock
                ? t('stock.label.stocks.usefulActualTooltip', { unit: store.unit })
                : t('stock.label.stocks.actualTooltip', { unit: store.unit }),
            value: projection => projection.actualStock != null ? Math.round(projection.actualStock).toString() : '',
            textAlign: 'right' as const,
            htmlFor: projection => <Actual
                projection={projection}
                stockFiltersStore={stockFiltersStore}
                classes={classes}
            />,
            ...withTourKey('stockProjectionTableActualColumn'),
            wrapHeaderText: true
        },
        ...(simulationStore.simulations.length > 0 && forSimulation ? [
            {
                name: t('stock.label.stocks.simulated'),
                tooltipText: t('stock.label.stocks.simulatedTooltip', { unit: store.unit }),
                value: projection => projection.simulatedStock ? Math.round(projection.simulatedStock).toString() : '',
                htmlFor: projection => simulatedProjection(projection, simulationStore.simulations.map(item => item.date), classes),
                textAlign: 'right' as const,
                ...withTourKey('stockProjectionTableSimulatedColumn')
            }, {
                name: t('stock.label.stocks.simulatedDays'),
                tooltipText: t('stock.label.stocks.simulatedDaysTooltip'),
                value: projection => projection.simulatedDaysLeft ? Math.round(projection.simulatedDaysLeft).toString() : '',
                htmlFor: projection => simulatedProjectionDays(projection, simulationStore.simulations.map(item => item.date), classes),
                textAlign: 'right' as const,
                ...withTourKey('stockProjectionTableSimulatedDaysColumn')
            }
        ] : [])
    ]
}

export let useProjectionColumns = (classes: any, forSimulation: boolean = false) => {
    let settings = UserSettingsContextContainer.useContainer()
    let store = StockBoardContainer.useContainer()
    let simulationStore = SimulationBoardContainer.useContainer()
    let stockFiltersStore = StockFiltersContainer.useContainer()
    let feature = FeatureContainer.useContainer()
    let [simulationEditing, setSimulationEditing] = useState<SimulationEditing>(null)
    let [simulatedValue, setSimulatedValue] = useState<StockSimulation | null>(null)

    let handleSimulationValidated = (validation?: StockSimulation | null) => {
        let toValidate = simulatedValue ?? validation
        if (toValidate)
            simulationStore.addOrUpdateSimulation(toValidate)
        setSimulationEditing(null)
        setSimulatedValue(null)
    }

    let changePurchaseSimulation = (simulation: StockSimulation, untriggeredVolume?: number | null) => {
        let purchaseVolumeWithoutUntriggered = simulation.value - (untriggeredVolume ?? 0)
        setSimulatedValue({ ...simulation, value: purchaseVolumeWithoutUntriggered })
    }

    let changeSimulation = (simulation: StockSimulation) => {
        setSimulatedValue(simulation)
    }

    let options: ProjectionColumnsOptions = {
        shortDateFormat: settings.getShortDateFormat(),
        usefulStock: stockFiltersStore.filters.usefulStock,
        store,
        simulationStore,
        stockFiltersStore,
        feature,
        classes,
        simulationEditing,
        setSimulationEditing,
        handleSimulationValidated,
        changePurchaseSimulation,
        changeSimulation
    }

    return {
        store,
        simulationStore,
        stockFiltersStore,
        columns: getProjectionColumns(options, forSimulation)
    }
}

function SimulationDate({ projection, shortDateFormat }: { projection: StockProjection, shortDateFormat: string }) {
    return <span>{moment(projection.date).format(shortDateFormat)}</span>
}

function ProjectionDate({ projection, shortDateFormat, changeDate }: {
    projection: StockProjection,
    shortDateFormat: string,
    changeDate: StockBoardStore['changeStockDateFilter']
}) {
    return (
        <Link tooltipText={'Apply filter'} onClick={() => changeDate(projection.date)}>
            {moment(projection.date).format(shortDateFormat)}
        </Link>
    )
}

let getMovementVolume = (projection: StockProjection, type: string): number | null | undefined =>
    projection.volumeTypes.find(x => x.type === type)?.volume

let getPurchaseValue = (projection: StockProjection): string => {
    let volume = getMovementVolume(projection, PurchaseType)
    let untriggeredVolume = getMovementVolume(projection, UntriggeredType)

    return untriggeredVolume
        ? Math.round(volume ?? 0 + untriggeredVolume).toString()
        : volume
            ? Math.round(volume).toString()
            : ''
}

function SimulationPurchase({ projection, options }: { projection: StockProjection, options: ProjectionColumnsOptions }) {
    let simulation = options.simulationStore.simulations.find(item => item.date === projection.date && item.movementType === PurchaseType)
    let purchaseVolume = getMovementVolume(projection, PurchaseType)
    let untriggeredVolume = getMovementVolume(projection, UntriggeredType)
    let volume = untriggeredVolume
        ? (purchaseVolume ?? 0) + untriggeredVolume
        : purchaseVolume

    if (simulation) {
        let simulationWithUntriggered = { ...simulation, value: simulation.value + (untriggeredVolume ?? 0) }
        return fieldSimulated(projection, simulationWithUntriggered, PurchaseType, options.classes)
    }

    return fieldWithSimulation({
        volume,
        date: projection.date,
        simulation,
        addSimulation: simulation => options.changePurchaseSimulation(simulation, untriggeredVolume),
        validateSimulation: options.handleSimulationValidated,
        isEditMode: isAfterToday(projection.date) && !!options.simulationEditing
            && options.simulationEditing.date === projection.date
            && options.simulationEditing.type === PurchaseType,
        type: PurchaseType
    }, options.classes)
}

function ProjectionPurchase({ projection, hasPurchaseDealDetail, onLinkClicked }: {
    projection: StockProjection,
    hasPurchaseDealDetail: boolean,
    onLinkClicked: () => void
}) {
    let value = getPurchaseValue(projection)
    return hasPurchaseDealDetail
        ? <Link onClick={onLinkClicked}> {value} </Link>
        : <span> {value} </span>
}

let openPurchaseDialog = (projection: StockProjection, options: ProjectionColumnsOptions) => {
    let args: PurchaseDealDetailArgs = {
        date: projection.date,
        productIds: options.stockFiltersStore.filters.productIds,
        dutyStatuss: options.stockFiltersStore.filters.dutyStatuses,
        companys: options.stockFiltersStore.filters.companies,
        movements: options.store.movements
            .filter(movement => movement.movementType == MovementType.Purchase && movement.date === projection.date)
    }
    purchaseDealDetailDialog.open(args)
}

let getProjectionPurchaseTotal = (values: StockProjection[] | null): number | undefined =>
    values?.reduce((acc, projection) =>
        acc + (getMovementVolume(projection, PurchaseType) ?? 0) + (getMovementVolume(projection, UntriggeredType) ?? 0), 0)

let getSimulationPurchaseTotal = (values: StockProjection[] | null, simulations: StockSimulation[]): number | undefined =>
    values?.reduce((acc, projection) => {
        let purchaseVolume = getMovementVolume(projection, PurchaseType) ?? 0
        let untriggeredVolume = getMovementVolume(projection, UntriggeredType) ?? 0
        let simulation = simulations.find(x => x.date === projection.date && x.movementType === PurchaseType)
        return acc + (simulation?.value ?? purchaseVolume) + untriggeredVolume
    }, 0)

let getMktSaleValue = (projection: StockProjection): string => {
    let volume = getMovementVolume(projection, MktSaleType)
    return volume ? Math.round(volume).toString() : ''
}

function SimulationMktSale({ projection, options }: { projection: StockProjection, options: ProjectionColumnsOptions }) {
    let simulation = options.simulationStore.simulations.find(item => item.date === projection.date && item.movementType === MktSaleType)
    let volume = getMovementVolume(projection, MktSaleType)

    if (simulation) {
        let simulationWithUntriggered = { ...simulation, value: simulation.value }
        return fieldSimulated(projection, simulationWithUntriggered, MktSaleType, options.classes)
    }

    return fieldWithSimulation({
        volume,
        date: projection.date,
        simulation,
        addSimulation: options.changeSimulation,
        validateSimulation: options.handleSimulationValidated,
        isEditMode: isAfterToday(projection.date) && !!options.simulationEditing
            && options.simulationEditing.date === projection.date
            && options.simulationEditing.type === MktSaleType,
        type: MktSaleType
    }, options.classes)
}

function ProjectionMktSale({ projection }: { projection: StockProjection }) {
    return <span>{getMktSaleValue(projection)}</span>
}

let getProjectionMktSaleTotal = (values: StockProjection[] | null): number | undefined =>
    values?.reduce((acc, projection) => acc + (getMovementVolume(projection, MktSaleType) || 0), 0)

let getSimulationMktSaleTotal = (values: StockProjection[] | null, simulations: StockSimulation[]): number | undefined =>
    values?.reduce((acc, projection) => {
        let volume = getMovementVolume(projection, MktSaleType) || 0
        let simulation = simulations.find(x => x.date === projection.date && x.movementType === MktSaleType)
        return acc + (simulation?.value ?? volume)
    }, 0)

function Transit({ projection, options }: { projection: StockProjection, options: ProjectionColumnsOptions }) {
    let props = buildTransitStockDetailProps(projection, options.stockFiltersStore)

    function displayTransitStockDetail() {
        if (!props.shouldDisplayTransitStockDetail || !props.date)
            return

        let args: TransitStockDetailArgs = {
            date: props.date,
            companys: props.companys,
            dutyStatuss: props.dutyStatuss,
            productIds: props.productIds,
            sites: props.sites
        }

        transitStockDetailDialog.open(args)
    }

    let volume = props.volumeInTransit ? Math.round(props.volumeInTransit).toString() : ''
    return <Link onClick={displayTransitStockDetail}>{volume}</Link>
}

let notEmpty = (collection: string[] | null | undefined): boolean =>
    collection != null && collection.length !== 0

let buildTransitStockDetailProps = (
    projection: StockProjection,
    stockFiltersStore: StockFiltersStore
): TransitStockDetailProps => {
    let shouldDisplay = notEmpty(stockFiltersStore.filters.dutyStatuses)
        && notEmpty(stockFiltersStore.filters.productIds)
        && notEmpty(stockFiltersStore.filters.companies)
        && notEmpty(stockFiltersStore.filters.sites)

    return {
        volumeInTransit: projection.volumeInTransit,
        date: projection.date,
        companys: stockFiltersStore.filters.companies,
        dutyStatuss: stockFiltersStore.filters.dutyStatuses,
        sites: stockFiltersStore.filters.sites,
        productIds: stockFiltersStore.filters.productIds,
        shouldDisplayTransitStockDetail: shouldDisplay
    }
}

function Calculated({ projection, classes }: { projection: StockProjection, classes: any }) {
    return projection.isProjectedPartial
        ? <Tooltip title={<Typography variant='body1'>{t('stock.label.partialProjected')}</Typography>} placement='top'>
            <span className={classes.mixedField}>{!!projection.calculatedStock ? Math.round(projection.calculatedStock).toString() : ''}</span>
        </Tooltip>
        : <span>{projection.calculatedStock != null ? Math.round(projection.calculatedStock).toString() : ''}</span>
}

let shouldDisplayProjectedStockTableAverage = (options: ProjectionColumnsOptions): boolean => {
    let { stockFiltersStore, store, feature } = options
    let filterStartDate = moment(stockFiltersStore.filters.start).format(isoDateFormat)
    let filterEndDate = moment(stockFiltersStore.filters.end).format(isoDateFormat)
    let startOfMonth = moment(stockFiltersStore.filters.end).startOf('month').format(isoDateFormat)
    let endOfMonth = moment(stockFiltersStore.filters.end).endOf('month').format(isoDateFormat)
    let isFilterFullMonthSelected = filterStartDate === startOfMonth && filterEndDate === endOfMonth

    return isFilterFullMonthSelected
        && feature.hasFeature('ProjectedStockTableAverage')
        && !!store.stockProjectionResult.compulsoryStockLines?.length
}

let getDecadeAverageProjectedStock = (options: ProjectionColumnsOptions): number => {
    let { stockFiltersStore, store } = options
    let tenthOfTheMonth = moment(stockFiltersStore.filters.start).add(9, 'd').format(isoDateFormat)
    let twentyOfTheMonth = moment(tenthOfTheMonth).add(10, 'd').format(isoDateFormat)
    let endOfTheMonth = moment(stockFiltersStore.filters.start).endOf('month').format(isoDateFormat)

    let getDateProjectedStock = (formattedDate: string): number =>
        store.stockProjectionResult.values?.find(item => item.date.startsWith(formattedDate))?.calculatedStock ?? 0

    return [getDateProjectedStock(tenthOfTheMonth),
    getDateProjectedStock(twentyOfTheMonth),
    getDateProjectedStock(endOfTheMonth)].reduce((sum, value) => sum + value) / 3
}

let getStockPercent = (projection: StockProjection, stockPercentHigh: boolean): string => {
    let value = stockPercentHigh ? projection.highPercent : projection.maxPercent
    return value ? Math.round(value * 100).toString() + '%' : ''
}

function Days({ projection, minDays, classes }: { projection: StockProjection, minDays: number | null, classes: any }) {
    return minDays && projection.daysLeft && projection.daysLeft <= minDays
        ? <span className={classes.lowStockDays}>{!!projection.daysLeft ? Math.trunc(projection.daysLeft) : ''}</span>
        : <span>{!!projection.daysLeft ? Math.trunc(projection.daysLeft) : ''}</span>
}

let computeStockVariation = (store: StockBoardStore): number => {
    let purchaseTotal = getProjectionPurchaseTotal(store.stockProjectionResult.values) ?? 0
    let mktSalesTotal = getProjectionMktSaleTotal(store.stockProjectionResult.values) ?? 0
    let otherTotal = store.stockProjectionResult.values?.reduce((acc, projection) => acc + (projection.volumeOther || 0), 0) ?? 0
    return purchaseTotal - mktSalesTotal + otherTotal
}

function Actual({ projection, stockFiltersStore, classes }: {
    projection: StockProjection,
    stockFiltersStore: StockFiltersStore,
    classes: any
}) {
    let props = buildActualStockCalibrationProps(projection, stockFiltersStore)
    let options = { tooltip: '', className: '' }
    switch (props.actualStockStatus) {
        case 'FullyCalibrated': options = { tooltip: 'stock.label.calibration', className: classes.calibrationField }; break
        case 'NotCalibrated': options = { tooltip: 'stock.label.mixed', className: classes.mixedField }; break
        case 'Mixed': options = { tooltip: 'stock.label.mixed', className: classes.mixedField }; break
        case 'Opened': options = { tooltip: 'stock.label.opened', className: classes.openedField }; break
        case 'Frozen': options = { tooltip: 'stock.label.frozen', className: classes.frozenField }; break
        case 'Closed': options = { tooltip: 'stock.label.closed', className: classes.closedField }; break
        default: options = { tooltip: '', className: '' }
    }

    function displayStockProjectionDetails(date: string | null) {
        if (!date || !props.shouldDisplayStockProjectionDetails) return

        let args: StockProjectionDetailArgs = {
            date: props.date,
            companys: props.companies,
            dutyStatuss: props.dutyStatuses,
            productIds: props.productIds,
            sites: props.sites
        }

        stockProjectionDetailDialog.open(args)
    }

    return (
        <Link tooltipText={t(options.tooltip)}
            className={options.className}
            onClick={() => displayStockProjectionDetails(props.date)}>
            {props.actualStock != null ? Math.round(props.actualStock).toString() : ''}
        </Link>
    )
}

let buildActualStockCalibrationProps = (
    projection: StockProjection,
    stockFiltersStore: StockFiltersStore
): ActualStockCalibrationProps => {
    let shouldDisplayStockProjectionDetails = notEmpty(stockFiltersStore.filters.dutyStatuses)
        && notEmpty(stockFiltersStore.filters.productIds)
        && notEmpty(stockFiltersStore.filters.companies)
        && notEmpty(stockFiltersStore.filters.sites)
        && hasClaim(Claims.StockManager)

    return {
        actualStock: projection.actualStock,
        actualStockStatus: projection.actualStockStatus,
        date: projection.date,
        shouldDisplayStockProjectionDetails: shouldDisplayStockProjectionDetails ?? false,
        companies: stockFiltersStore.filters.companies,
        dutyStatuses: stockFiltersStore.filters.dutyStatuses,
        sites: stockFiltersStore.filters.sites,
        productIds: stockFiltersStore.filters.productIds
    }
}

export let projectionColumnStyles = {
    calibrationField: {
        color: defaultColors.lightBlue.light.color,
        fontWeight: 'bold'
    },
    mixedField: {
        color: defaultColors.orange.light.color,
        fontWeight: 'bold'
    },
    openedField: {
        color: openedColor,
        fontWeight: 'bold'
    },
    frozenField: {
        color: frozenColor,
        fontWeight: 'bold'
    },
    closedField: {
        color: closedColor,
        fontWeight: 'bold'
    },
    lowStockDays: {
        color: defaultColors.red.light.color,
        fontWeight: 'bold'
    },
    simulationField: {
        maxWidth: '8em'
    },
    simulatedProjected: {
        color: defaultColors.lightBlue.light.color,
        fontWeight: 'bold'
    }
}
